import React, { useState, useRef, useEffect } from 'react';
import './MessageEditor.css';

const API_BASE = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? 'https://intranet.nesimltd.co.ke' : 'http://localhost:5000');

const MessageEditor = ({ 
  message, 
  roomId, 
  token, 
  onSave, 
  onCancel,
  currentUser 
}) => {
  const [editedContent, setEditedContent] = useState(message.content || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const textareaRef = useRef(null);

  // Focus and select text when component mounts
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.select();
    }
  }, []);

  // Handle save
  const handleSave = async () => {
    console.log('🔧 [EDITOR] handleSave called');
    console.log('🔧 [EDITOR] editedContent:', editedContent);
    console.log('🔧 [EDITOR] original content:', message.content);
    console.log('🔧 [EDITOR] roomId:', roomId);
    console.log('🔧 [EDITOR] messageId:', message._id);
    console.log('🔧 [EDITOR] token:', token ? 'present' : 'missing');
    
    if (!editedContent.trim()) {
      console.log('🔧 [EDITOR] Content is empty, aborting');
      setError('Message cannot be empty');
      return;
    }

    if (editedContent.trim() === message.content) {
      console.log('🔧 [EDITOR] No changes made, canceling');
      onCancel(); // No changes made
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const url = `${API_BASE}/api/chat/rooms/${roomId}/messages/${message._id}`;
      console.log('🔧 [EDITOR] Making PUT request to:', url);
      
      const response = await fetch(
        url,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            content: editedContent.trim()
          })
        }
      );

      console.log('🔧 [EDITOR] Response status:', response.status);
      console.log('🔧 [EDITOR] Response ok:', response.ok);

      if (response.ok) {
        const data = await response.json();
        console.log('🔧 [EDITOR] Edit successful, calling onSave with:', data.updatedMessage);
        onSave(data.updatedMessage);
      } else {
        const errorData = await response.json();
        console.error('🔧 [EDITOR] Edit failed:', errorData);
        throw new Error(errorData.message || 'Failed to edit message');
      }
    } catch (err) {
      console.error('🔧 [EDITOR] Error editing message:', err);
      setError(err.message || 'Failed to edit message');
    } finally {
      setSaving(false);
    }
  };

  // Handle keyboard shortcuts
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      onCancel();
    }
  };

  // Calculate remaining characters
  const remainingChars = 2000 - editedContent.length;
  const isOverLimit = remainingChars < 0;

  return (
    <div className="message-editor">
      <div className="editor-container">
        <textarea
          ref={textareaRef}
          value={editedContent}
          onChange={(e) => setEditedContent(e.target.value)}
          onKeyDown={handleKeyDown}
          className={`editor-textarea ${isOverLimit ? 'over-limit' : ''}`}
          placeholder="Edit your message..."
          disabled={saving}
          rows={Math.min(Math.max(editedContent.split('\n').length, 2), 6)}
        />
        
        <div className="editor-footer">
          <div className="editor-info">
            <span className={`char-count ${isOverLimit ? 'over-limit' : ''}`}>
              {remainingChars} characters remaining
            </span>
            {error && <span className="error-message">{error}</span>}
          </div>
          
          <div className="editor-actions">
            <button 
              className="editor-btn cancel-btn" 
              onClick={onCancel}
              disabled={saving}
            >
              Cancel
            </button>
            <button 
              className="editor-btn save-btn" 
              onClick={handleSave}
              disabled={saving || !editedContent.trim() || isOverLimit}
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>
      
      <div className="editor-help">
        <span className="help-text">
          Press <kbd>Enter</kbd> to save, <kbd>Esc</kbd> to cancel
        </span>
      </div>
    </div>
  );
};

export default MessageEditor;
